import React, { useEffect, useState } from "react";

import { Formik, Form, FieldArray } from "formik";
import { Edit, Delete, Plus } from "icons";

import ProviderManagerTemplatesApi from "apis/providerManagerTemplate";
import { Button, Pane, Typography } from "clearsense-ui/components";
import { Select, Input } from "clearsense-ui/components/formik";

import { FIELD_TYPE } from "../constants";
import { ADD_ASSOCIATION_TEMPLATE_VALIDATION } from "../validations";

const CreateAssociation = ({
  isCreateAssociationTemplate,
  setIsCreateAssociationTemplate,
  editAssociationDetails,
  setEditAssociationDetails,
  setEditRecordId,
  getProviderManagerTemplates,
}) => {
  const [templates, setTemplate] = useState();
  const [toEntities, setToEntities] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [previousData, setPreviousData] = useState({});

  const getTemplateName = (templates, templateId) =>
    templates.filter(template => template.id === templateId)[0]?.name;

  const handleFromTemplateChange = (selectedTemplate, setFieldValue) => {
    const fromTemplate = templates.filter(
      template => template.id === selectedTemplate.value
    )[0];
    const associatedTemplateIds = fromTemplate?.associations?.map(
      association => association.to
    );
    setFieldValue("fromEntity", selectedTemplate);
    setFieldValue("toEntity", null);
    setFieldValue("fromTemplateAssociations", fromTemplate?.associations);
    associatedTemplateIds
      ? setToEntities(
          templates
            .filter(template => !associatedTemplateIds?.includes(template.id))
            .filter(template => template.id !== selectedTemplate.value)
        )
      : setToEntities(
          templates.filter(template => template.id !== selectedTemplate.value)
        );
  };

  const handleToTemplateChange = (selectedTemplate, setFieldValue) => {
    const toTemplate = templates.filter(
      template => template.id === selectedTemplate.value
    )[0];
    setFieldValue("toEntity", selectedTemplate);
    setFieldValue("toTemplateAssociations", toTemplate?.associations);
  };

  const getTemplateOptions = templates =>
    templates?.map(template => ({
      label: template.entity,
      value: template.id,
    }));

  const handleFieldSave = (errors, index, setFieldValue) => {
    const fieldErrors = errors?.fields?.[index];
    const hasNoError = fieldErrors?.length === 0 || fieldErrors === undefined;
    if (hasNoError) {
      setFieldValue(`fields.${index}.isEdit`, false);
      setFieldValue(`fields.${index}.isInitial`, false);
    }
  };

  const handleSaveCancel = (arrayHelpers, field, index, setFieldValue) => {
    if (field.isInitial) {
      arrayHelpers.remove(index);
    } else {
      setFieldValue(`fields.${index}.isEdit`, false);
      setFieldValue(`fields.${index}.name`, previousData?.[index]?.name);
      setFieldValue(`.fields.${index}.type`, previousData?.[index]?.type);
      setPreviousData(previousData => delete { ...previousData }.index);
    }
  };

  const handleTemplateSave = async data => {
    const fromTemplateAssociations = data?.fromTemplateAssociations
      ? [...data.fromTemplateAssociations]
      : [];
    const toTemplateAssociations = data?.toTemplateAssociations
      ? [...data.toTemplateAssociations]
      : [];

    const templateFields = {
      fields: data.fields.map(field => ({
        name: field.name,
        type: field.type.value,
      })),
    };

    const fromTemplateTransformedData = {
      associations: [
        ...fromTemplateAssociations,
        {
          to: data.toEntity.value,
          ...templateFields,
        },
      ],
    };
    const toTemplateTransformedData = {
      associations: [
        ...toTemplateAssociations,
        {
          to: data.fromEntity.value,
          ...templateFields,
        },
      ],
    };

    try {
      await ProviderManagerTemplatesApi.update({
        grouped_templates: [
          { id: data.fromEntity.value, ...fromTemplateTransformedData },
          { id: data.toEntity.value, ...toTemplateTransformedData },
        ],
      });
      getProviderManagerTemplates();
      setIsCreateAssociationTemplate(false);
      setEditAssociationDetails();
      setEditRecordId();
      getEntities();
    } catch (error) {
      logger.error(error);
    }
  };

  const INITIAL_VALUE = editAssociationDetails
    ? {
        fromEntity: {
          label: getTemplateName(templates, editAssociationDetails.from),
          value: editAssociationDetails.from,
        },
        toEntity: {
          label: getTemplateName(
            templates,
            editAssociationDetails.association.to
          ),
          value: editAssociationDetails.association.to,
        },
        fields: editAssociationDetails.association.fields.map(field => ({
          name: field.name,
          type: { value: field.type, label: field.type },
          isEdit: false,
          isInitial: false,
        })),
      }
    : {
        fromEntity: null,
        toEntity: null,
        fields: [{ name: "", type: undefined, isEdit: true, isInitial: true }],
      };

  const getEntities = async () => {
    try {
      setIsLoading(true);
      const { data } = await ProviderManagerTemplatesApi.fetch();
      setTemplate(data);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEntities();
  }, []);

  return (
    <Formik
      initialValues={INITIAL_VALUE}
      validationSchema={ADD_ASSOCIATION_TEMPLATE_VALIDATION}
      onSubmit={handleTemplateSave}
    >
      {({ values, errors, setFieldValue, submitForm, resetForm }) => (
        <Form className="w-full" noValidate>
          <Pane
            isOpen={isCreateAssociationTemplate}
            onClose={() => {
              setEditAssociationDetails();
              setEditRecordId();
              setIsCreateAssociationTemplate(false);
            }}
          >
            <Pane.Header>
              <Typography style="h3" type="semibold" className="-mt-3">
                {editAssociationDetails ? "Edit" : "Create"} an Association
              </Typography>
              <Typography style="large">
                Associate template with other template
              </Typography>
            </Pane.Header>
            <Pane.Body>
              <Select
                name="fromEntity"
                label="From entity"
                placeholder="Select from entity"
                isDisabled={isLoading || editAssociationDetails}
                className="flex-grow-0 w-full mt-6"
                options={getTemplateOptions(templates)}
                onChange={selectedOption =>
                  handleFromTemplateChange(selectedOption, setFieldValue)
                }
                required
              />
              <Select
                name="toEntity"
                label="To entity"
                placeholder="Select to entity"
                isDisabled={
                  isLoading || !values.fromEntity || editAssociationDetails
                }
                className="flex-grow-0 w-full mt-6"
                options={getTemplateOptions(toEntities)}
                onChange={selectedOption =>
                  handleToTemplateChange(selectedOption, setFieldValue)
                }
                required
              />
              <FieldArray name="fields">
                {arrayHelpers => (
                  <>
                    {values?.fields?.map((field, fieldIndex) => {
                      if (!field?.isEdit) {
                        return (
                          <div
                            key={fieldIndex}
                            className="flex justify-between items-center p-3 border rounded-sm border-content-300 w-full mt-2 bg-grey-blue"
                          >
                            <div>
                              <Typography
                                type="semibold"
                                className="text-content-darker dark:text-white leading-4"
                              >
                                Field {`${field.name}`}
                              </Typography>
                              <Typography className=" leading-4 text-content-800">
                                type: {`${field.type.label}`}
                              </Typography>
                            </div>

                            <Button
                              icon={Edit}
                              style="text"
                              iconSize="24"
                              onClick={() => {
                                setFieldValue(
                                  `fields.${fieldIndex}.isEdit`,
                                  true
                                );
                                setPreviousData(previousData => ({
                                  ...previousData,
                                  [fieldIndex]: {
                                    name: field?.name,
                                    type: field?.type,
                                  },
                                }));
                              }}
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          className=" p-3 border border-shade-light w-full mt-4"
                          key={fieldIndex}
                        >
                          <Typography
                            style="condensedMedium"
                            className="text-content-darker dark:text-white mb-4"
                          >
                            Add a Field
                          </Typography>
                          <Input
                            name={`fields.${fieldIndex}.name`}
                            label="Field Name"
                            placeholder="Enter Name"
                            required
                          />
                          <Select
                            name={`fields.${fieldIndex}.type`}
                            options={FIELD_TYPE}
                            label="Field Type"
                            placeholder="Select Type"
                            className="mt-4"
                            required
                          />
                          <div className=" flex gap-2 mt-4">
                            <Button
                              label="Save field"
                              onClick={() =>
                                handleFieldSave(
                                  errors,
                                  fieldIndex,
                                  setFieldValue
                                )
                              }
                            />
                            <Button
                              label="Cancel"
                              style="secondary"
                              disabled={
                                values.fields[fieldIndex].isInitial &&
                                values.fields.length <= 1
                              }
                              onClick={() =>
                                handleSaveCancel(
                                  arrayHelpers,
                                  field,
                                  fieldIndex,
                                  setFieldValue
                                )
                              }
                            />
                            {!field.isInitial && (
                              <Button
                                icon={Delete}
                                style="secondary"
                                className="text-red-700"
                                onClick={() => arrayHelpers.remove(fieldIndex)}
                                disabled={values.fields.length <= 1}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <Button
                      label="Add Fields"
                      icon={Plus}
                      style="secondary"
                      className="mt-4"
                      disabled={!(values.fromEntity && values.toEntity)}
                      onClick={() => {
                        arrayHelpers.push({
                          name: "",
                          type: "",
                          isEdit: true,
                          isInitial: true,
                        });
                      }}
                    />
                  </>
                )}
              </FieldArray>
            </Pane.Body>
            <Pane.Footer className="space-x-4">
              <Button
                label="Save"
                type="submit"
                disabled={values?.fields?.length === 0}
                onClick={submitForm}
              />
              <Button
                label="Cancel"
                style="secondary"
                size="medium"
                onClick={() => {
                  setIsCreateAssociationTemplate(false);
                  resetForm();
                }}
              />
            </Pane.Footer>
          </Pane>
        </Form>
      )}
    </Formik>
  );
};

export default CreateAssociation;
